import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import moment from 'moment';
import config from '../../../config';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import TpmMaintenanceList from './TpmMaintenanceList';
import TpmTipsModal from './TpmTipsModal';
import { getStoredUser, getSessionHeaders } from './tpmRequest';
import ToolingMasterDataPagination from './ToolingMasterDataPagination';
import { TOOLING_TABLE_STYLES } from './toolingTableStyles';

const DEFAULT_ROWS_PER_PAGE = 10;

function getChecklistRows(body) {
    if (Array.isArray(body)) return body;
    return body && Array.isArray(body.data) ? body.data : [];
}

function getTotalRows(body, rows) {
    if (body && body.meta && Number.isFinite(Number(body.meta.total_count))) return Number(body.meta.total_count);
    if (body && body.pagination && Number.isFinite(Number(body.pagination.total))) return Number(body.pagination.total);
    return rows.length;
}

function formatUpdatedDate(row) {
    const value = row && (row.updatedAt || row.updated_at || row.updatedDate || row.updated_date || row.createdAt || row.created_at);
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

function getInterval(row, tierKey) {
    let tier = row && row[tierKey];
    if (typeof tier === 'string') {
        try {
            tier = JSON.parse(tier);
        } catch (_error) {
            tier = null;
        }
    }
    if (tier && typeof tier === 'object') return tier.intervalPcs || '';
    const legacyValue = row && row[`${tierKey}IntervalPcs`];
    return legacyValue === null || legacyValue === undefined ? '' : legacyValue;
}

export default function ToolingTPM() {
    const [activeTab, setActiveTab] = useState('checklist');
    const [showTips, setShowTips] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [activeKeyword, setActiveKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [choosingAsset, setChoosingAsset] = useState(null);
    const requestSequence = useRef(0);

    const loadChecklists = useCallback((page, limit, searchKeyword) => {
        const user = getStoredUser();
        if (!user || !user.accountId) {
            setRows([]);
            setTotalRows(0);
            setError('Please sign in to view TPM checklists.');
            return Promise.resolve();
        }

        const offset = (page - 1) * limit;
        const requestId = ++requestSequence.current;
        const params = new URLSearchParams({
            company: String(user.accountId),
            keyword: searchKeyword || '',
            limit: String(limit),
            offset: String(offset)
        });

        setLoading(true);
        setError('');
        return fetch(`${config.apiUrl}/getToolingTpmAssets?${params.toString()}`, {
            method: 'GET',
            headers: getSessionHeaders(user)
        })
            .then(async response => {
                const text = await response.text();
                let body = {};
                try {
                    body = text ? JSON.parse(text) : {};
                } catch (_error) {
                    body = {};
                }
                if (!response.ok) throw new Error(body.error || body.message || 'Failed to load TPM checklists.');
                return body;
            })
            .then(body => {
                if (requestId !== requestSequence.current) return;
                const records = getChecklistRows(body);
                setRows(records);
                setTotalRows(getTotalRows(body, records));
                setLoading(false);
            })
            .catch(requestError => {
                if (requestId !== requestSequence.current) return;
                setRows([]);
                setTotalRows(0);
                setError(requestError.message || 'Failed to load TPM checklists.');
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        loadChecklists(currentPage, rowsPerPage, activeKeyword);
    }, [activeKeyword, currentPage, loadChecklists, rowsPerPage]);

    const columns = [
        { name: 'Part No', selector: row => row.partNo || '', sortable: true },
        { name: 'Tooling No', selector: row => row.toolingNo || '', sortable: true },
        { name: 'Checklist', selector: row => row.checklists.length > 1 ? `${row.checklists.length} checklists` : row.checklists.length ? 'Active' : 'Not configured' },
        { name: 'Tier A (PCS)', selector: row => row.checklists.length === 1 ? getInterval(row.checklists[0], 'tierA') : '', sortable: true },
        { name: 'Tier B (PCS)', selector: row => row.checklists.length === 1 ? getInterval(row.checklists[0], 'tierB') : '', sortable: true },
        { name: 'Tier C (PCS)', selector: row => row.checklists.length === 1 ? getInterval(row.checklists[0], 'tierC') : '', sortable: true },
        { name: 'Updated', selector: row => row.checklists.length === 1 ? formatUpdatedDate(row.checklists[0]) : '', sortable: true },
        {
            name: 'Actions',
            cell: row => row.checklists.length > 1 ? (
                <Button variant="link" className="p-0" onClick={() => setChoosingAsset(row)}>Edit checklist</Button>
            ) : (
                <Link to={`/tooling/tpm/checklist/${row.checklists[0]?.id || 'new'}?toolingId=${encodeURIComponent(row.id)}`}>Edit checklist</Link>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            width: '160px'
        }
    ];

    const isChecklistTab = activeTab === 'checklist';

    return (
        <Card className="m-3">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <h1 className="mb-0">Tooling Preventive Maintenance</h1>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" type="button" onClick={() => setShowTips(true)}>Tier Tips</Button>
                    </div>
                </div>

                <div role="tablist" className="d-flex gap-2 mb-3">
                    <Button
                        role="tab"
                        aria-selected={isChecklistTab}
                        variant={isChecklistTab ? 'primary' : 'outline-primary'}
                        onClick={() => setActiveTab('checklist')}
                    >
                        Checklists
                    </Button>
                    <Button
                        role="tab"
                        aria-selected={!isChecklistTab}
                        variant={!isChecklistTab ? 'primary' : 'outline-primary'}
                        onClick={() => setActiveTab('maintenance')}
                    >
                        Maintenance records
                    </Button>
                </div>

                {isChecklistTab ? (
                    <div>
                        {error && <div className="alert alert-danger" role="alert">{error}</div>}
                        <Form className="d-flex gap-2 mb-3" onSubmit={event => {
                            event.preventDefault();
                            setCurrentPage(1);
                            setActiveKeyword(keyword);
                        }}>
                            <Form.Control
                                aria-label="Search TPM checklists"
                                value={keyword}
                                placeholder="Search..."
                                onChange={event => setKeyword(event.target.value)}
                            />
                            <Button type="submit">Search</Button>
                        </Form>
                        <DataTable
                            columns={columns}
                            data={rows}
                            customStyles={TOOLING_TABLE_STYLES}
                            striped
                            noDataComponent="No tooling assets match this search."
                            progressPending={loading}
                            progressComponent={<DatacoLoadingIndicator loading={loading} size="compact" label="Loading TPM checklists" />}
                            pagination
                            paginationServer
                            paginationComponent={ToolingMasterDataPagination}
                            paginationTotalRows={totalRows}
                            paginationPerPage={rowsPerPage}
                            paginationDefaultPage={currentPage}
                            onChangePage={page => setCurrentPage(page)}
                            onChangeRowsPerPage={(limit, page) => {
                                setRowsPerPage(limit);
                                setCurrentPage(page);
                            }}
                            highlightOnHover
                        />
                    </div>
                ) : (
                    <TpmMaintenanceList />
                )}

                <TpmTipsModal show={showTips} onHide={() => setShowTips(false)} />
                <Modal show={!!choosingAsset} onHide={() => setChoosingAsset(null)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Select checklist for {choosingAsset?.toolingNo}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Choose the checklist to edit.</p>
                        {choosingAsset?.checklists.map(checklist => (
                            <div key={checklist.id} className="mb-2">
                                <Link to={`/tooling/tpm/checklist/${checklist.id}?toolingId=${encodeURIComponent(choosingAsset.id)}`}>
                                    {checklist.partNo || 'No Part No'} · Checklist {checklist.id}
                                </Link>
                            </div>
                        ))}
                    </Modal.Body>
                </Modal>
            </Card.Body>
        </Card>
    );
}
